import { Clock3, Grid2X2, TrendingUp, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@sshop/i18n';
import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';
import { headerSearchPanelStyles as s } from './HeaderSearchPanel.styles';
import { HeaderSearchResults } from './HeaderSearchResults';
import type { SearchPanelProps } from './HeaderSearchPanel.types';

export function HeaderSearchPanel(props: SearchPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const suggestions = ['iPhone', 'Samsung', 'PlayStation 5', 'Dyson', 'AirPods', 'LG'];
  if (props.query.length >= 2) return <HeaderSearchResults {...props} />;
  return (
    <div ref={props.panelRef} className={s.panel} id="header-search-suggestions">
      <div className={s.columns}>
        <aside data-search-scroll="filters" className={s.aside}>
          <section aria-label={t('search.recent')}>
            <div className={s.historyHeader}>
              <h2 className={s.historyHeading}>{t('search.recent')}</h2>
              {props.history.length > 0 && (
                <button type="button" className={s.textButton} onClick={props.onClearHistory}>
                  {t('search.clearHistory')}
                </button>
              )}
            </div>
            {props.history.length === 0 ? (
              <p className={s.empty}>{t('search.noRecent')}</p>
            ) : (
              <ul id="header-search-options" role="listbox" aria-label={t('search.recent')}>
                {props.history.map((query, index) => (
                  <li key={query} className={s.historyRow}>
                    <button
                      type="button"
                      id={`header-search-history-${index}`}
                      role="option"
                      aria-selected={index === props.activeIndex}
                      className={s.historyItem}
                      onClick={() => props.onSelect(query)}
                    >
                      <Clock3 className={s.iconGlyph} aria-hidden />
                      <span className={s.historyTerm}>{query}</span>
                    </button>
                    <button
                      type="button"
                      className={s.historyRemove}
                      aria-label={t('search.removeRecent', { query })}
                      onClick={() => props.onRemoveHistory(query)}
                    >
                      <X className={s.iconGlyph} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section aria-label={t('search.suggested')}>
            <h2 className={s.heading}>{t('search.suggested')}</h2>
            <div className={s.chips}>
              {suggestions.map((query) => (
                <button
                  key={query}
                  type="button"
                  className={s.suggestedChip}
                  onClick={() => props.onSelect(query)}
                >
                  <TrendingUp className={s.iconGlyph} aria-hidden />
                  {query}
                </button>
              ))}
            </div>
          </section>
        </aside>
        <section
          data-search-scroll="products"
          className={s.main}
          aria-label={t('search.popularCategories')}
        >
          <div className={s.sectionHeader}>
            <h2 className={s.headingTitle}>{t('search.popularCategories')}</h2>
            <Link
              to={routes.categories(locale)}
              className={s.textButton}
              onClick={props.onNavigate}
            >
              {t('home.categories.viewAll')}
            </Link>
          </div>
          <div className={s.categoryGrid}>
            {props.categories.map((category) => (
              <Link
                key={category.id}
                to={routes.catalog(locale, category.slug)}
                className={s.categoryTile}
                onClick={props.onNavigate}
              >
                {category.imageUrl ? (
                  <img src={category.imageUrl} alt="" className={s.categoryImage} loading="lazy" />
                ) : (
                  <Grid2X2 className={s.categoryFallback} aria-hidden />
                )}
                <span className={s.categoryName}>{category.name}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
